import {
  type BaseFlowBlock,
  type FlowBlockType,
  type FlowCareGapAlgorithmBlock,
  type FlowConditionalBlock,
  type FlowConversationBlock,
  type FlowCreateEncounterSummaryBlock,
  type FlowCreateNotableObjectBlock,
  type FlowCreatePatientOrderBlock,
  type FlowDecorationData,
  FlowDefinitionState,
  type FlowDocumentExtractBlock,
  type FlowDocumentSearchBlock,
  type FlowDuration,
  type FlowEvaluationBlock,
  type FlowExportTextToBucketBlock,
  type FlowExportToSftpBlockConfig,
  type FlowFhirBlock,
  type FlowFileConversionBlock,
  type FlowFilterListBlock,
  type FlowGenerateFileBlock,
  type FlowGetExternalDocumentPacketBlock,
  type FlowGetFormSetBlockConfig,
  type FlowGetInsurancesBlock,
  type FlowGetPatientOrderBlock,
  type FlowGetSftpFileBlock,
  type FlowHieGetDocumentsBlock,
  type FlowHieQueryDocumentsBlock,
  type FlowHumanTaskBlock,
  type FlowInformationExtractionBlockConfig,
  type FlowInputSchema,
  type FlowInsuranceCardBlock,
  type FlowLlmPromptBlock,
  type FlowMedicalNecessityCheckBlock,
  type FlowMirthBlock,
  type FlowOcrV2Block,
  type FlowOutputBlock,
  type FlowPatientDataStoreBlockConfig,
  type FlowProducedType,
  type FlowRealTimeEligibilityBlock,
  type FlowRestBlock,
  type FlowSchedulingBlock,
  type FlowScriptedFlowBlock,
  type FlowSendEmailBlock,
  type FlowStartBlock,
  type FlowSwitchBlock,
  type FlowTextBlock,
  type FlowUpdateFlowConfigBlock,
  type FlowUpdateNotableObjectBlock,
  type FlowVoiceCallReturnData,
  type FlowVoiceCallV2ReturnData,
  type FlowWaitBlockV2,
  type FlowWaitForFormCompletionBlock,
  type FlowWaitForSignalBlock,
} from 'flow-schema';

import { type BaseItem, type NullablePartial } from '../common.js';
import { type AdvancedFhirBlock } from './advancedFhir/config.js';
import { type AuthorizationSubmissionBlock } from './authorizationSubmission/configs.js';
import { type AgentConversationResponse } from './conversation/flowTypes.js';
import { type DocumentTemplateBlock } from './documentTemplate/configs.js';
import { type ExportExternalDocumentPacketBlockConfig } from './exportExternalDocumentPacket/configs.js';
import { type FaxBlock } from './fax/configs.js';
import { type FileReference } from './file.js';
import { type BaseFlow, type Flow, type UpdateValues as FlowUpdateValues } from './flow.js';
import { type AgentCategory } from './flowAgent.js';
import { type FormBlock } from './form/config.js';
import { type FormV2Block } from './form/configV2.js';
import { type GetNotableObjectBlock } from './getNotableObject/config.js';
import { type GetProviderBlock } from './getProvider/config.js';
import { type HccAlgorithmBlock } from './hccAlgorithm/hccAlgorithmBlock.js';
import { type Hl7Block } from './hl7/configs.js';
import { type HumanTaskActivityReturnType } from './humanTask/returnType.js';
import { type IteratorV1Block } from './iterator/v1/config.js';
import { type IteratorV2Block } from './iterator/v2/config.js';
import { type MontageOnBaseBlock } from './montageOnBase/config.js';
import { type OcrBlock } from './ocr/config.js';
import { type OcrDetailResponse } from './ocr/flowTypes.js';
import type * as PatientDataStoreTypes from './patientDataStore/flowTypes.js';
import { type RepeatBlock } from './repeat/config.js';
import { type RestApiClassicAuthorization } from './restApiClassicAuthorization/config.js';
import { type SendEmailBlockOutput } from './sendEmail/config.js';
import { type SendSmsBlock } from './sendSms/config.js';
import { type ServiceReviewBlock } from './serviceReview/config.js';
import { type SkillV2Block } from './skill/index.js';
import { type SubFlowV1Block } from './subFlow/v1/config.js';
import { type SubFlowV2Block } from './subFlow/v2/config.js';
import { type VoiceCallBlockConfig } from './voiceCall/config.js';
import { type VoiceCallV2Block } from './voiceCall/configV2.js';
import { type WaitBlock } from './wait/index.js';
import { type EncounterSummary } from '../encounter/summary.js';
import { type Encounter } from '../encounter.js';
import { type ExternalDocumentPacket } from '../externalDocumentPacket.js';
import { type Finding } from '../patient/finding.js';
import { type BasePatient, type Sex } from '../patient.js';
import { type Kind } from '../rpaJob.js';
import { type User } from '../user.js';

export * from './advancedFhir/config.js';
export * from './authorizationSubmission/configs.js';
export * from './conversation/configs.js';
export * from './documentTemplate/configs.js';
export * from './exportExternalDocumentPacket/configs.js';
export * from './fax/configs.js';
export * from './form/config.js';
export * from './form/configV2.js';
export * from './fileConversion/config.js';
export * from './getProvider/config.js';
export * from './hccAlgorithm/hccAlgorithmBlock.js';
export * as HieGetDocuments from './hieGetDocuments/config.js';
export * from './hl7/configs.js';
export * from './humanTask/index.js';
export * from './knowledgeBaseFile.js';
export * from './montageOnBase/config.js';
export * from './repeat/config.js';
export * from './rest/config.js';
export * from './sendEmail/config.js';
export * from './sendSms/config.js';
export * from './serviceReview/config.js';
export * from './subFlow/v1/config.js';
export * from './subFlow/v2/config.js';
export * from './subFlow/v2/flowArgument.js';
export * from './skill/index.js';
export * from './updateNotableObject/config.js';
export * from './iterator/v1/config.js';
export * from './iterator/v2/config.js';
export * from './getNotableObject/config.js';
export * from './restApiClassicAuthorization/config.js';
export * from './careGapAlgorithm/config.js';
export * as PatientDataStore from './patientDataStore/config.js';
export * from './base/baseSchema.js';
export * from './base/keywords.js';
export * from './ocr/config.js';
export * from './conversation/configs.js';
export * from './voiceCall/modelDescriptions.js';
export * from './voiceCall/config.js';
export * from './voiceCall/configV2.js';
export * from './voiceCall/transferToolConfig.js';
export * from './wait/index.js';
export * as Conditional from './conditional/index.js';
export * from './blockCompatibility/index.js';

export const FlowDefinitionStateProps: Record<
  FlowDefinitionState,
  {
    name: string;
    isEditable: boolean;
    isPrePublishState: boolean;
    isCurrentState: boolean;
  }
> = {
  [FlowDefinitionState.Draft]: {
    name: 'Draft',
    isEditable: true,
    isPrePublishState: true,
    isCurrentState: true,
  },
  [FlowDefinitionState.Review]: {
    name: 'Review',
    isEditable: false,
    isPrePublishState: true,
    isCurrentState: true,
  },
  [FlowDefinitionState.Published]: {
    name: 'Published',
    isEditable: false,
    isPrePublishState: false,
    isCurrentState: true,
  },
  [FlowDefinitionState.Superseded]: {
    name: 'Superseded',
    isEditable: false,
    isPrePublishState: false,
    isCurrentState: false,
  },
  [FlowDefinitionState.SoftDeleted]: {
    name: 'Deleted',
    isEditable: false,
    isPrePublishState: false,
    isCurrentState: false,
  },
};

export interface FlowData {
  blocks: FlowBlock[];
  edges: FlowEdge[];
  decorations?: FlowDecorationData;
  inputSchema?: FlowInputSchema;
}

export interface FlowTag {
  name: string;
  category?: string | null;
}

interface BaseFlowDefinition {
  flowId: string;
  version: number;
  state: FlowDefinitionState;
  /**
   * Per-version creator: the user who started this specific FlowDefinition
   * (e.g. the user who cloned the previous published version into a new draft).
   * Distinct from `Flow.createdById`, which records the original Flow's creator
   * and does not change across versions.
   */
  createdById: string | null;
  /**
   * Per-version last updater: the user who most recently mutated this
   * FlowDefinition. Distinct from `Flow.updatedById`, which only advances when
   * Flow-level fields (name, description, organizationId, agentId, tags) change.
   */
  updatedById: string | null;
  data: FlowData;
  lastSnapshotId: string | null; // FK to snapshots, set to NULL when data is mutated
  notes: string | null;
  documentationMarkdown?: string | null;
}

export interface FlowDefinition extends BaseItem, BaseFlowDefinition {}

export interface FlowDefinitionWithUsers extends FlowDefinition {
  /** Email of the per-version {@link BaseFlowDefinition.createdById}. */
  createdByEmail: string | null;
  /** Email of the per-version {@link BaseFlowDefinition.updatedById}. */
  updatedByEmail: string | null;
}

/**
 * This is a temporary interface to allow us to migrate to separating Flows from FlowDefinitions.
 */
export interface FlowDefinitionWithFlow extends FlowDefinitionWithUsers, Omit<Flow, 'state'> {}

export type FlowDefinitionWithFlowBasic = Omit<FlowDefinitionWithFlow, 'data'>;

export type SubflowDefinitionWithFlow = Pick<
  FlowDefinitionWithFlow,
  | 'id'
  | 'flowId'
  | 'version'
  | 'name'
  | 'description'
  | 'kind'
  | 'state'
  | 'organizationId'
  | 'notes'
  | 'documentationMarkdown'
  | 'data'
>;

export interface SkillDefinitionWithSkill {
  skill: Pick<Flow, 'id' | 'name' | 'description' | 'kind'>;
  flowDefinitions: SubflowDefinitionWithFlow[];
}

export interface FlowDefinitionForList extends FlowDefinitionWithFlow {
  runs: number;
}

interface CreateValuesWithFlow extends Omit<BaseFlowDefinition, 'flowId' | 'version' | 'state'> {
  // The omitted fields get set to defaults on create
  tags?: FlowTag[] | null;
}

type BaseFlowSansStateAndAgentId = Omit<BaseFlow, 'state' | 'agentId'>;
export interface UpdateValuesWithFlow
  extends
    Omit<FlowUpdateValues, 'state' | 'kind' | 'updatedById' | 'clonedFromFlowId'>,
    Pick<Partial<BaseFlowDefinition>, 'data' | 'notes' | 'state' | 'documentationMarkdown'> {}

/**
 * Defining an interface for creating flow def + flow together until versioning is worked out.
 * flowId is not specified because we create it in {@link createFlowWithDefinition}
 **/

// Flow creation with existing agent
export type CreateFlowWithDefinitionValuesExistingAgent = NullablePartial<CreateValuesWithFlow> &
  NullablePartial<BaseFlowSansStateAndAgentId> & {
    agentId: string | null;
  };

// Flow creation with new agent
export type CreateFlowWithDefinitionValuesNewAgent = NullablePartial<CreateValuesWithFlow> &
  NullablePartial<BaseFlowSansStateAndAgentId> & {
    agent: {
      name: string;
      description?: string;
      category?: AgentCategory;
      organizationId: string;
      state?: string;
    };
  };

// Union type for both valid payloads
export type CreateFlowWithDefinitionValues =
  CreateFlowWithDefinitionValuesExistingAgent | CreateFlowWithDefinitionValuesNewAgent;

export type FlowDefinitionFindAllOptions = Partial<{
  name: string; // checked by ilike
  ids: string[];
  organizationId: string | string[];
  createdBefore: string;
  createdAfter: string;
  createdByEmail: string;
  agentId: string;
  agentIds: string[];
  updatedByEmail: string;
  state: FlowDefinitionState;
  includeInactive: boolean;
  flowId: string;
  version: number;
  includesSubFlowDefinitionId: string;
  limit: number;
  offset: number;
  orderBy: string;
  orderDirection: 'asc' | 'desc';
  orderSpec: string;
  snapshotIds: string[];
}>;

export type FlowBlock =
  | AdvancedFhirBlock
  | AsynchronousExportJobBlock
  | AsynchronousExtractJobBlock
  | CallbackBlock
  | FlowConditionalBlock
  | FlowCareGapAlgorithmBlock
  | FlowConversationBlock
  | FlowCreateEncounterSummaryBlock
  | FlowCreateNotableObjectBlock
  | FlowCreatePatientOrderBlock
  | FlowDocumentExtractBlock
  | FlowDocumentSearchBlock
  | DocumentTemplateBlock
  | FlowEvaluationBlock
  | ExportExternalDocumentPacketBlock
  | FlowExportTextToBucketBlock
  | ExportToSftpBlock
  | FaxBlock
  | FlowFhirBlock
  | FlowFileConversionBlock
  | FlowFilterListBlock
  | FormBlock
  | FormV2Block
  | FlowGenerateFileBlock
  | GetExternalDocumentPacketBlock
  | GetFormSetBlock
  | FlowGetInsurancesBlock
  | GetPatientBlock
  | FlowGetPatientOrderBlock
  | GetNotableObjectBlock
  | GetProviderBlock
  | FlowGetSftpFileBlock
  | FlowRealTimeEligibilityBlock
  | HccAlgorithmBlock
  | FlowHieGetDocumentsBlock
  | FlowHieQueryDocumentsBlock
  | Hl7Block
  | FlowHumanTaskBlock
  | InformationExtractionBlock
  | FlowInsuranceCardBlock
  | IteratorV1Block
  | IteratorV2Block
  | RepeatBlock
  | FlowLlmPromptBlock
  | MedicalNecessityCheckBlock
  | FlowMirthBlock
  | MontageOnBaseBlock
  | OcrBlock
  | FlowOcrV2Block
  | FlowOutputBlock
  | PatientDataStoreBlock<FlowProducedType>
  | FlowRestBlock
  | RestApiClassicAuthorization
  | FlowSchedulingBlock
  | FlowScriptedFlowBlock
  | FlowSendEmailBlock
  | SendSmsBlock
  | ServiceReviewBlock
  | FlowStartBlock
  | SubFlowV1Block
  | SubFlowV2Block
  | SkillV2Block
  | FlowSwitchBlock
  | FlowTextBlock
  | FlowUpdateFlowConfigBlock
  | FlowUpdateNotableObjectBlock
  | VoiceCallBlock
  | VoiceCallV2Block
  | VoiceTranscribeBlock
  | AuthorizationSubmissionBlock
  | WaitBlock
  | FlowWaitBlockV2
  | FlowWaitForSignalBlock
  | FlowWaitForFormCompletionBlock;

// In backend ConsumerOf includes a consumesType field
export interface ConsumerOf {
  sourceBlockName: string;
}

export interface PatientDataStoreBlock<T extends FlowProducedType> extends BaseFlowBlock<T> {
  type: FlowBlockType.PatientDataStore;
  config: FlowPatientDataStoreBlockConfig;
}

export interface ProducedTypeMap {
  [FlowProducedType.Any]: any;
  [FlowProducedType.Start]: StartBlockOutput;
  [FlowProducedType.SubFlowV1Result]: SubFlowBlockData;
  [FlowProducedType.Patient]: PatientWithEhrInformationId;
  [FlowProducedType.LabValues]: { data: unknown[] };
  [FlowProducedType.EncounterSummary]: EncounterSummary;
  [FlowProducedType.ExternalDocumentPacket]: ExternalDocumentPacket;
  [FlowProducedType.Practitioner]: Record<string, unknown>;
  [FlowProducedType.Provider]: User;
  [FlowProducedType.Encounter]: Encounter;
  [FlowProducedType.Encounters]: Record<string, unknown>;
  [FlowProducedType.Appointments]: Record<string, unknown>;
  [FlowProducedType.ServiceRequests]: Record<string, unknown>;
  [FlowProducedType.Conditions]: { data: unknown[] };
  [FlowProducedType.Medications]: { data: unknown[] };
  [FlowProducedType.DocumentMetadata]: { data: unknown[] };
  [FlowProducedType.DocumentBinaries]: { data: unknown[] };
  [FlowProducedType.DocumentExtract]: unknown;
  [FlowProducedType.SurgicalHistories]: { data: unknown[] };
  [FlowProducedType.PatientFindings]: Finding[];
  [FlowProducedType.VoiceCall]: FlowVoiceCallReturnData;
  [FlowProducedType.VoiceCallV2]: FlowVoiceCallV2ReturnData;
  [FlowProducedType.VoiceTranscribe]: Record<string, unknown>;
  [FlowProducedType.Evaluation]: Record<string, unknown>;
  [FlowProducedType.Coverages]: Record<string, unknown>;
  [FlowProducedType.PdsLabValues]: {
    data: PatientDataStoreTypes.LabValueResponse[];
  };
  [FlowProducedType.PdsEncounters]: {
    data: PatientDataStoreTypes.EncounterResponse[];
  };
  [FlowProducedType.PdsMedications]: {
    data: PatientDataStoreTypes.MedicationResponse[];
  };
  [FlowProducedType.PdsDocumentMetadata]: {
    data: PatientDataStoreTypes.DocumentMetadataResponse[];
  };
  [FlowProducedType.PdsDocumentBinaries]: {
    data: PatientDataStoreTypes.DocumentResponse[];
  };
  [FlowProducedType.PdsDocumentTranscriptions]: {
    data: PatientDataStoreTypes.DocumentTranscriptionResponse[];
  };
  [FlowProducedType.PdsSurgicalHistories]: {
    data: PatientDataStoreTypes.SurgicalHistoryResponse[];
  };
  [FlowProducedType.PdsConditions]: {
    data: PatientDataStoreTypes.ConditionResponse[];
  };
  [FlowProducedType.FileReference]: FileReference;
  [FlowProducedType.OcrDetailResponse]: OcrDetailResponse;
  [FlowProducedType.AgentConversation]: AgentConversationResponse;
  [FlowProducedType.HumanTask]: HumanTaskActivityReturnType;
  [FlowProducedType.SendEmail]: SendEmailBlockOutput;
}

export interface SubFlowBlockData {
  result?: any;
}

export interface GetFormSetBlock extends BaseFlowBlock<FlowProducedType.Any> {
  type: FlowBlockType.GetFormSet;
  config: FlowGetFormSetBlockConfig;
}

export interface GetPatientBlock extends BaseFlowBlock<FlowProducedType.Patient> {
  type: FlowBlockType.GetPatient;
  config: {
    returnType: FlowProducedType.Patient;
  };
}

export interface GetExternalDocumentPacketBlock extends BaseFlowBlock<FlowProducedType.ExternalDocumentPacket> {
  type: FlowBlockType.GetExternalDocumentPacket;
  config: FlowGetExternalDocumentPacketBlock['config'];
}

export interface ExportExternalDocumentPacketBlock extends BaseFlowBlock<FlowProducedType.Any> {
  type: FlowBlockType.ExportExternalDocumentPacket;
  config: ExportExternalDocumentPacketBlockConfig;
}

export interface ExportToSftpBlock extends BaseFlowBlock<FlowProducedType.Any> {
  type: FlowBlockType.ExportToSftp;
  config: FlowExportToSftpBlockConfig;
}

export interface VoiceCallBlock extends BaseFlowBlock<FlowProducedType.VoiceCall> {
  type: FlowBlockType.VoiceCall;
  config: VoiceCallBlockConfig;
}

export interface CallbackBlock extends BaseFlowBlock<FlowProducedType.Any> {
  type: FlowBlockType.Callback;
  config: {
    message: string;
    waitTime: FlowDuration;
    timeout: FlowDuration;
  };
}

export interface InformationExtractionBlock extends BaseFlowBlock<FlowProducedType.Any> {
  type: FlowBlockType.InformationExtraction;
  config: FlowInformationExtractionBlockConfig;
}

export interface VoiceTranscribeBlock extends BaseFlowBlock<FlowProducedType.VoiceTranscribe> {
  type: FlowBlockType.VoiceTranscribe;
  config: {
    storagePath: string;
    transcriptionType: string;
  };
}

export interface MedicalNecessityCheckBlock extends BaseFlowBlock<FlowProducedType.Any> {
  type: FlowBlockType.MedicalNecessityCheck;
  config: FlowMedicalNecessityCheckBlock['config'];
}

export interface FlowEdge {
  source: string;
  target: string;

  /** When an edge is from a Condition to a block, it defines the path for the condition value it represents **/
  condition?: string;

  /** True when an edge exists as the error path **/
  isError?: boolean;
}

export interface AsynchronousExtractJobBlock extends BaseFlowBlock<FlowProducedType.Any> {
  type: FlowBlockType.AsynchronousExtractJob;
  config: {
    jobKind: Kind;
    timeout: FlowDuration;
  };
}

export interface AsynchronousExportJobBlock extends BaseFlowBlock<FlowProducedType.Any> {
  type: FlowBlockType.AsynchronousExportJob;
  config: {
    timeout: FlowDuration;
    v2?: boolean;
  } & (
    | {
        jobKind: Kind.ExportPrevisitPlanning;
        payload: {
          content: string;
          patientEhrId: string;
          patientFirstName: string;
          patientLastName: string;
          patientDateOfBirth: Date;
          patientSex: Sex;
          encounterEhrReferenceValue: string;
        };
      }
    | {
        jobKind: Kind.ExportDummyValues;
        payload: {
          foo: string;
        };
      }
  );
}

export interface PatientWithEhrInformationId extends BasePatient {
  ehrInformationId: string;
}

export interface StartBlockOutput {
  organizationId: string;
  practiceId: string;
  entityType?: string;
  entityId?: string;
  entity?: unknown;
  startData?: unknown;
}

export interface CycleDetection {
  sourceBlock: FlowBlock;
  cyclicBlock: FlowBlock;
}

export interface CodeMirrorCompletionSnippet {
  label: string;
  type?: string;
  boost: number;
}
